import React, { MouseEvent, useEffect } from 'react';
import { observer } from 'mobx-react';
import { Tooltip } from 'antd';
import { columnOption } from '../columnOption';
import cx from 'classnames';
import { IGetApiType } from '../../../REST';
import { IStatItem } from '../../../interfaces';
import {
    useCompanyInfo,
    useCompanyStatistics,
    useCurrencyShortCodeById,
    useDashboardActivityUsage,
    useProfile,
    useProfileSettings,
    useRouterStore,
    useUiLanguage,
} from '../../../hooks';
import {
    cleanCompanyStore,
    getCompanyById,
    getCompanyStatisticsById,
    profileBytesSignificantDigits,
} from '../../../middleware';
import { numberToStringWithSeparator, stringCount } from '../../../instruments';
import { ITrPage, tF, tI, tP } from '../../../translate';
import { Catcher, IShowMoneyItem, ShowMoneyList } from '../../../Components';
import { DashboardInfoCard } from './../Components';
import Styles from '../m_dashboarStyles.less';

interface IMyCompanyLink {
    companyId?: string;
}

interface IDashboardHeader extends IMyCompanyLink {
    idSpinners?: IGetApiType[];
    boxTitlePrefix?: ITrPage;
}

const MyCompanyLink: React.FC<IMyCompanyLink> = observer(({ companyId }) => {
    const languageId = useUiLanguage();
    const routerStore = useRouterStore();

    const clickLinkMore = (event: MouseEvent<HTMLButtonElement | HTMLAnchorElement>): void => {
        event.preventDefault();
        if (companyId) {
            routerStore.goTo('Company', { params: { id: companyId } });
        }
    };
    return (
        <Catcher>
            {companyId ? (
                <a onClick={clickLinkMore} href={`/companies/${companyId}`}>
                    {tI('More...')}
                </a>
            ) : null}
        </Catcher>
    );
});

export const DashboardHeader: React.FC<IDashboardHeader> = observer(
    ({ companyId, idSpinners = ['GET_ACTIVITY_ROLL', 'GET_COMPANY_STATISTICS'], boxTitlePrefix }) => {
        const languageId = useUiLanguage();
        const activityUsage = useDashboardActivityUsage();
        const { currency = '€', currencyId } = useCompanyInfo();
        const companyStatistics = useCompanyStatistics();
        const balanceDifference = companyStatistics?.balanceDifference ? +companyStatistics?.balanceDifference : 0;
        const balance = companyStatistics.balance ? +companyStatistics?.balance : 0;
        const shortCode = (currencyId && useCurrencyShortCodeById(currencyId)) || currency;
        const { isCompanyTypeIdMNOHost } = useProfile();
        const { hideCosts } = useProfileSettings();

        useEffect(() => {
            if (companyId) {
                getCompanyById(companyId);
                if (!isCompanyTypeIdMNOHost) {
                    getCompanyStatisticsById(companyId);
                }
            }

            return (): void => {
                cleanCompanyStore();
            };
        }, [companyId]);

        const textTitlePrefix = boxTitlePrefix ? tP(boxTitlePrefix) : '';
        const voice = activityUsage.voice;
        const voiceRatio = activityUsage.voiceRatio;

        const sms = activityUsage.sms;
        const smsRatio = activityUsage.smsRatio;

        const originalUnits = activityUsage.originalUnits;
        const originalUnitsRatio = activityUsage.originalUnitsRatio;

        const cardsCount = activityUsage.cardsCount;
        const cardsRatio = activityUsage.cardsRatio;

        const costRatio = activityUsage.costRatio;

        const CostShow = () => {
            const items = activityUsage.costs.map(
                (i: IStatItem): IShowMoneyItem => ({ value: i.cost, symbol: i.currencySymbol }),
            );
            let stylesFont: string;
            switch (items.length) {
                case 1:
                case 2:
                case 4: {
                    stylesFont = Styles.font_1_8;
                    break;
                }
                case 3:
                case 5:
                case 6: {
                    stylesFont = Styles.font_1_2;
                    break;
                }
                default: {
                    stylesFont = Styles.font_1;
                }
            }
            return (
                <div className={cx(Styles.valueColumns, stylesFont)}>
                    <ShowMoneyList items={items} classNameItem={Styles.valueColumnsItem} />
                </div>
            );
        };

        const cost = <CostShow />;

        return (
            <Catcher>
                <div className={Styles.cardsPanel}>
                    <div className={cx(Styles.cardsRow, Styles.activityUsageGrid)}>
                        {!isCompanyTypeIdMNOHost && !hideCosts && (
                            <DashboardInfoCard
                                idSpinners={idSpinners}
                                radio={costRatio}
                                title={`${textTitlePrefix}${textTitlePrefix ? ' ' + tI('by Cost') : tF('Cost')}`}
                                value={cost}
                            />
                        )}
                        {!isCompanyTypeIdMNOHost && (
                            <DashboardInfoCard
                                idSpinners={idSpinners}
                                more={<MyCompanyLink companyId={companyId} />}
                                radio={balanceDifference}
                                title={tF('Company Balance')}
                                value={`${shortCode} ${numberToStringWithSeparator(balance, 2)}`}
                                layoutOptions={columnOption.dashboardInfoCardBig}
                            />
                        )}
                        <DashboardInfoCard
                            idSpinners={idSpinners}
                            radio={cardsRatio}
                            title={`${textTitlePrefix}${
                                textTitlePrefix ? ' ' + tI('by Active Cards') : tF('Active Cards')
                            }`}
                            value={numberToStringWithSeparator(cardsCount)}
                        />
                        <DashboardInfoCard
                            idSpinners={idSpinners}
                            radio={originalUnitsRatio}
                            title={`${textTitlePrefix}${
                                textTitlePrefix ? ' ' + tI('by Data Usage') : tF('Data Usage')
                            }`}
                            value={
                                <Tooltip title={`${stringCount(originalUnits)} byte`}>
                                    <span>
                                        {originalUnits ? profileBytesSignificantDigits(originalUnits, 2) : '0 byte'}
                                    </span>
                                </Tooltip>
                            }
                        />
                        <DashboardInfoCard
                            idSpinners={idSpinners}
                            radio={smsRatio}
                            title={`${textTitlePrefix}${textTitlePrefix ? ' ' + tI('by Sms Usage') : tF('Sms Usage')}`}
                            value={`${numberToStringWithSeparator(Number(sms))} sms`}
                        />
                        <DashboardInfoCard
                            idSpinners={idSpinners}
                            radio={voiceRatio}
                            title={`${textTitlePrefix}${
                                textTitlePrefix ? ' ' + tI('by Voice Usage') : tF('Voice Usage')
                            }`}
                            value={
                                <Tooltip title={`${stringCount(voice)} sec`}>
                                    <span>
                                        {voice
                                            ? `${numberToStringWithSeparator(Math.floor(voice / 60))} min ${
                                                  voice % 60
                                              } sec`
                                            : '0 sec'}
                                    </span>
                                </Tooltip>
                            }
                        />
                    </div>
                </div>
            </Catcher>
        );
    },
);
